import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    Avatar,
    Chip,
    Button,
    Alert,
    CircularProgress,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Divider
} from '@mui/material';
import {
    Group as GroupIcon,
    Star as LeaderIcon,
    ArrowBack as BackIcon,
    Person as PersonIcon,
    SportsEsports as GameIcon,
    Event as EventIcon,
    AccessTime as TimeIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import gulagService from '../services/gulag';
import { resolveMediaUrl } from '../utils/mediaUrl';

function SessionLobby() {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadSessionLobby();
        // Optionally, poll for updates every 10 seconds
        const interval = setInterval(loadSessionLobby, 10000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);

    const loadSessionLobby = async () => {
        if (!sessionId) return;

        // Only show loading on initial load
        if (!session) {
            setLoading(true);
        }
        setError(null);

        try {
            const result = await gulagService.getSessionLobby(sessionId);
            
            if (result.success) {
                setSession(result.data);
            } else {
                setError(result.error);
            }
        } catch (err) {
            console.error('Error loading session lobby:', err);
            setError('Failed to load session lobby');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'open_for_registration': return 'info';
            case 'started': return 'success';
            case 'in_progress': return 'warning';
            case 'awaiting_results': return 'warning';
            case 'completed': return 'success';
            case 'cancelled': return 'error';
            default: return 'default';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'open_for_registration': return 'Registration Open';
            case 'started': return 'Started';
            case 'in_progress': return 'In Progress';
            case 'awaiting_results': return 'Awaiting Results';
            case 'completed': return 'Completed';
            case 'cancelled': return 'Cancelled';
            default: return status;
        }
    };

    const formatDateTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString([], {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button
                    startIcon={<BackIcon />}
                    onClick={() => navigate(-1)}
                    variant="contained"
                >
                    Go Back
                </Button>
            </Container>
        );
    }

    if (!session) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="warning">
                    Session not found
                </Alert>
                <Button
                    startIcon={<BackIcon />}
                    onClick={() => navigate(-1)}
                    variant="contained"
                    sx={{ mt: 2 }}
                >
                    Go Back
                </Button>
            </Container>
        );
    }

    const isOrganizer = session.organizer_id === (user?.id || user?._id);

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Button
                    startIcon={<BackIcon />}
                    onClick={() => navigate(-1)}
                    sx={{ mb: 2 }}
                >
                    Back to Sessions
                </Button>
                
                <Paper sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                                {session.title}
                            </Typography>
                            {session.description && (
                                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                                    {session.description}
                                </Typography>
                            )}
                            
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                                <Chip
                                    icon={<GameIcon />}
                                    label={session.game_name || 'Unknown Game'}
                                    color="primary"
                                />
                                <Chip
                                    icon={<EventIcon />}
                                    label={session.event_name || 'Unknown Event'}
                                />
                                <Chip
                                    icon={<TimeIcon />}
                                    label={formatDateTime(session.scheduled_time)}
                                />
                                <Chip
                                    icon={<PersonIcon />}
                                    label={`Organizer: ${session.organizer_name}`}
                                />
                            </Box>
                        </Box>
                        
                        <Chip
                            label={getStatusLabel(session.status)}
                            color={getStatusColor(session.status)}
                            sx={{ fontWeight: 600, fontSize: '1rem', py: 3 }}
                        />
                    </Box>
                </Paper>
            </Box>

            {/* Groups */}
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <GroupIcon />
                Groups & Participants
            </Typography>

            {session.groups && session.groups.length > 0 ? (
                <Grid container spacing={3}>
                    {session.groups.map((group) => (
                        <Grid item xs={12} md={6} key={group.group_id}>
                            <Card sx={{ height: '100%' }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {group.group_name}
                                        </Typography>
                                        <Chip
                                            label={`${group.participants.length} / ${group.max_participants}`}
                                            color="primary"
                                            size="small"
                                        />
                                    </Box>

                                    {group.leader_id && (
                                        <Alert
                                            severity="info"
                                            icon={<LeaderIcon />}
                                            sx={{ mb: 2 }}
                                        >
                                            <strong>Group Leader:</strong> {group.leader_name}
                                            <br />
                                            <Typography variant="caption">
                                                This player will host the game lobby
                                            </Typography>
                                        </Alert>
                                    )}

                                    <Divider sx={{ my: 2 }} />

                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Participants:
                                    </Typography>

                                    <List dense>
                                        {group.participants.map((participant) => (
                                            <ListItem key={participant.user_id}>
                                                <ListItemAvatar>
                                                    <Avatar
                                                        src={resolveMediaUrl(participant.avatar)}
                                                        sx={{ width: 40, height: 40 }}
                                                    >
                                                        {participant.username.charAt(0).toUpperCase()}
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={participant.username}
                                                    secondary={new Date(participant.joined_at).toLocaleString()}
                                                />
                                                {group.leader_id === participant.user_id && (
                                                    <Chip
                                                        icon={<LeaderIcon />}
                                                        label="Leader"
                                                        color="primary"
                                                        size="small"
                                                    />
                                                )}
                                            </ListItem>
                                        ))}
                                    </List>

                                    {/* Show rankings if completed */}
                                    {group.rankings && group.rankings.length > 0 && (
                                        <>
                                            <Divider sx={{ my: 2 }} />
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                Final Rankings:
                                            </Typography>
                                            <List dense>
                                                {[...group.rankings]
                                                    .sort((a, b) => a.placement - b.placement)
                                                    .map((ranking) => (
                                                        <ListItem key={ranking.user_id}>
                                                            <ListItemText
                                                                primary={
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        <Chip
                                                                            label={`#${ranking.placement}`}
                                                                            size="small"
                                                                            color={ranking.placement === 1 ? 'success' : 'default'}
                                                                        />
                                                                        {ranking.username}
                                                                    </Box>
                                                                }
                                                                secondary={`${ranking.points_awarded.toFixed(0)} points awarded`}
                                                            />
                                                        </ListItem>
                                                    ))}
                                            </List>
                                        </>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <Alert severity="info">
                    Groups have not been configured yet. The organizer needs to configure groups before starting the session.
                </Alert>
            )}

            {/* Organizer Actions */}
            {isOrganizer && session.status === 'open_for_registration' && (
                <Box sx={{ mt: 4 }}>
                    <Alert severity="info">
                        As the organizer, you can configure groups and assign leaders before starting the session.
                    </Alert>
                </Box>
            )}
        </Container>
    );
}

export default SessionLobby;

